import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactGA from 'react-ga4';

// Styled Components
const LandingContainer = styled.div`
  font-family: 'Poppins', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background: linear-gradient(to right, #2D3142 70%, #8B6EB4 100%);
  color: white;
  padding: 6rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  max-width: 650px;
  
  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const EmotionalBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid var(--purple);
`;

const Heading = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  span {
    color: var(--purple);
    position: relative;
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  line-height: 1.6;
  font-weight: 300;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--purple);
  color: white;
  font-weight: 700;
  padding: 1.25rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: white;
    color: var(--purple);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
`;

const HeroImageContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: perspective(1000px) rotateY(-5deg);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: perspective(1000px) rotateY(0);
  }
`;

const TestimonialSection = styled.section`
  padding: 6rem 0;
  background-color: #f8f9fa;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #2D3142;
  text-align: center;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background-color: var(--purple);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #6C757D;
  margin: 2rem auto 3rem;
  text-align: center;
  max-width: 800px;
`;

const TestimonialCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  position: relative;
  
  &:before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 5rem;
    color: rgba(139, 110, 180, 0.1);
    font-family: Georgia, serif;
    line-height: 1;
  }
  
  .quote {
    font-style: italic;
    font-size: 1.1rem;
    color: #4a4a4a;
    line-height: 1.7;
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 1;
  }
  
  .author {
    display: flex;
    align-items: center;
    
    .avatar {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      margin-right: 1rem;
      object-fit: cover;
    }
    
    .info {
      .name {
        font-weight: 700;
        color: #2D3142;
        margin-bottom: 0.25rem;
      }
      
      .role {
        font-size: 0.9rem;
        color: #6C757D;
      }
    }
  }
`;

const BenefitsSection = styled.section`
  padding: 6rem 0;
  background-color: white;
`;

const BenefitCard = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
  
  .icon {
    width: 70px;
    height: 70px;
    background-color: rgba(139, 110, 180, 0.1);
    color: var(--purple);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 2rem;
    font-size: 2rem;
    flex-shrink: 0;
    
    @media (max-width: 768px) {
      margin-right: 0;
      margin-bottom: 1.5rem;
      margin-left: auto;
      margin-right: auto;
    }
  }
  
  .content {
    h3 {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 0.75rem;
      color: #2D3142;
    }
    
    p {
      color: #6C757D;
      margin-bottom: 0;
      line-height: 1.6;
    }
  }
`;

const CTASection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #8B6EB4 0%, #6A4C9C 100%);
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  span {
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 8px;
      background-color: rgba(255, 255, 255, 0.3);
      z-index: -1;
    }
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: white;
  color: var(--purple);
  font-weight: 700;
  padding: 1.25rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: #2D3142;
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const GiftLandingPageEmotional = () => {
  // Google Analytics tracking code setup
  useEffect(() => {
    // Track page view for this landing page
    ReactGA.send({
      hitType: "pageview",
      page: "/gift-landing-emotional",
      title: "Gift Landing Page - Emotional"
    });

    // Event tracking for CTA clicks
    const trackCTAClick = () => {
      ReactGA.event({
        category: 'engagement',
        action: 'gift_cta_click',
        label: 'Gift Landing Page Emotional CTA',
        value: 1
      });
    };
    // Add event listeners to CTA buttons
    const ctaButtons = document.querySelectorAll('.gift-cta-button');
    ctaButtons.forEach(button => {
      button.addEventListener('click', trackCTAClick);
    });

    return () => {
      // Clean up event listeners
      ctaButtons.forEach(button => {
        button.removeEventListener('click', trackCTAClick);
      });
    };
  }, []);

  return (
    <LandingContainer>
      <HeroSection>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <HeroContent>
                <EmotionalBadge>
                  <i className="bi bi-heart me-2"></i>
                  A GIFT THAT TRULY MATTERS
                </EmotionalBadge>
                <Heading>
                  GIVE THE GIFT OF <span>PEACEFUL NIGHTS</span> TO THE PARENTS WHO NEED IT MOST
                </Heading>
                <SubHeading>
                  Sleep deprivation affects 68% of new parents, impacting their mental health, relationships, and ability to enjoy parenthood. Your thoughtful gift can transform their daily lives.
                </SubHeading>
                <ButtonWrapper>
                  <CtaButton to="/gift" className="gift-cta-button">
                    GIVE THE GIFT OF SLEEP
                  </CtaButton>
                </ButtonWrapper>
              </HeroContent>
            </div>
            <div className="col-lg-5">
              <HeroImageContainer>
                <HeroImage 
                  src="/images/kelly-sikkema-TtsKN6qSf9A-unsplash.jpg" 
                  alt="Exhausted parent with baby" 
                />
              </HeroImageContainer>
            </div>
          </div>
        </div>
      </HeroSection>

      <TestimonialSection>
        <div className="container">
          <SectionTitle>STORIES FROM GRATEFUL PARENTS</SectionTitle>
          <SectionSubtitle>
            See how the gift of sleep has transformed the lives of these families.
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-md-6">
              <TestimonialCard>
                <p className="quote">
                  "My sister gifted us a sleep plan when our son was 6 months old and we were at our breaking point. Within a week, he was sleeping through the night. It wasn't just about sleep—it gave us our relationship back."
                </p>
                <div className="author">
                  
                  <div className="info">
                    <p className="name">Sarah M.</p>
                    <p className="role">Mother of 6-month-old</p>
                  </div>
                </div>
              </TestimonialCard>
            </div>
            
            <div className="col-md-6">
              <TestimonialCard>
                <p className="quote">
                  "As new parents, we were struggling with severe sleep deprivation. My colleague gifted us a Dozzy sleep plan, and it was honestly the most thoughtful gift we received. It changed everything for us."
                </p>
                <div className="author">
                  
                  <div className="info">
                    <p className="name">James & Leah</p>
                    <p className="role">Parents of 9-month-old twins</p>
                  </div>
                </div>
              </TestimonialCard>
            </div>
          </div>
        </div>
      </TestimonialSection>

      <BenefitsSection>
        <div className="container">
          <SectionTitle>WHY THIS GIFT MATTERS</SectionTitle>
          <SectionSubtitle>
            The gift of sleep is more than just rest—it's emotional wellbeing, stronger relationships, and renewed joy in parenthood.
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <BenefitCard>
                <div className="icon">
                  <i className="bi bi-heart-pulse"></i>
                </div>
                <div className="content">
                  <h3>Mental Health & Wellbeing</h3>
                  <p>
                    Sleep deprivation is linked to postpartum depression, anxiety, and reduced emotional resilience. Your gift helps protect a new parent's mental health during one of life's most challenging transitions.
                  </p>
                </div>
              </BenefitCard>
              
              <BenefitCard>
                <div className="icon">
                  <i className="bi bi-people"></i>
                </div>
                <div className="content">
                  <h3>Stronger Relationships</h3>
                  <p>
                    When sleep improves, so do relationships. Parents who sleep better report less conflict, more patience with their children, and more quality time together as a couple and family.
                  </p>
                </div>
              </BenefitCard>
              
              <BenefitCard>
                <div className="icon">
                  <i className="bi bi-stars"></i>
                </div>
                <div className="content">
                  <h3>Joy in Parenthood</h3>
                  <p>
                    Sleep-deprived parents often struggle to enjoy the parenting journey. Better sleep means more energy to be present, playful, and engaged with their little one during this precious time.
                  </p>
                </div>
              </BenefitCard>
            </div>
          </div>
        </div>
      </BenefitsSection>

      <CTASection>
        <div className="container">
          <CTATitle>
            GIVE A GIFT THAT <span>CHANGES LIVES</span>
          </CTATitle>
          <CTADescription>
            Your thoughtful gift of a personalized sleep plan will be remembered long after the baby clothes are outgrown and the toys are forgotten.
          </CTADescription>
          <CTAButtonWrapper>
            <CTAButton to="/gift" className="gift-cta-button">
              GIVE THE GIFT OF SLEEP
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </CTASection>
    </LandingContainer>
  );
};

export default GiftLandingPageEmotional;
